import jQuery from "jquery";
import Server from "../server";
import View from "../framework/view";
import {InventoryUse, Order} from "../bo/order";
import template from "../../pugc/orders";
import {
    BadgeNotification,
    CardNotification,
    MapOrdersNotification,
    PrintOrdersNotification,
    ViewOrderNotification
} from "./wrb";
import {add, format, parseISO, differenceInCalendarDays} from "date-fns";
import {hasExtras, isDelivery} from "../misc/orders";
import {AppFunction, badgeChange, DeliveryTime, FunctionBadge, JsonDate} from "../misc/types";
import {formatDate, parseDate, toJSONDate} from "../misc/dates";
import {formToJson, renderDollars} from "../misc/utils";
import Alerts from "../misc/alerts";
import {addDelivery, getDeliveries, removeDelivery} from "../misc/delivery";
import {OrderHistory} from "../bo/order_history";
import {Inventory} from "../bo/inventory";
import {subscribe, Subscription, sync} from "../resource_monitor";
import {renderName} from "../shop/product";

class PersistentState {

    name?: string;

    selectedIds : { [id: string]: boolean} = {};

    state = "New";

    delivery = "Any";

    dueDate: JsonDate | undefined;

    deliveryDateSince: JsonDate | undefined = toJSONDate(add(new Date(), {months: -1}));

    search : string | undefined = undefined;

    persist() {
        const x = JSON.stringify(this);
        window.localStorage.setItem(`${OrdersView.classname}-${this.name}`, x);
    }

    restore(name: string) {
        this.name = name;
        try {
            const x = window.localStorage.getItem(`${OrdersView.classname}-${this.name}`);
            if (x) {
                const y = JSON.parse(x);
                for (const z in y) {
                    if (z !== "deliveryDateSince") {
                        // @ts-ignore
                        this[z] = y[z];
                    }
                }
            }
        } catch (err) {
            console.log("Failed to restore", err);
        }
    }

}

type OrderFilter = (order: Order) => boolean;

export class OrdersView extends View {

    static classname = "orders-view";

    private readonly server = Server.instance();

    // -----------------
    // Configured state

    private name: string;

    private orders : Order[] = [];

    private noPast = false;

    private showFilters = true;

    private dueDates = new Set<JsonDate>();

    private today: JsonDate | undefined;

    private emptyOrderMessage : string | undefined;

    private excludeUndated = false;

    private filter: OrderFilter | undefined;

    private showInventory: boolean = false;

    private deliveryInventory: Inventory[] | undefined = undefined;
    private pickupInventory: Inventory[] | undefined = undefined;

    private syncPersistent: AppFunction | undefined;

    private lastBadge : FunctionBadge | undefined = undefined;

    private showGreenBadge = true;

    private subscription: Subscription | undefined = undefined;

    // -----------------
    // Derived state

    private filteredOrders : Order[] = [];

    private states : { [key: string] : number } = {};

    // ----------------
    // User state (persistent)

    private userState: PersistentState = new PersistentState();

    // ------------------
    // Internal state

    private deliveryAfter?: DeliveryTime;
    private deliveryBefore?: DeliveryTime;
    private setClockOrderId?: string;

    constructor(name: string, forNew: boolean, today?: JsonDate) {
        super(OrdersView.classname);

        this.name = name;

        this.userState.restore(name);

        if (today) {
            this.setToday(today);
        }
        if (forNew) {
            this.configForNew();
        }

        this.listen();
    }

    private configForNew() {
        this.showFilters = false;
        this.userState.state = "New";
        this.emptyOrderMessage = "There are no new orders."
        this.showGreenBadge = false;
        //this.noPast = true;
    }

    public configForEvent() {
        this.userState.state = "All";
    }

    public setFilter(f: OrderFilter | undefined) {
        this.filter = f;
    }

    public setShowInventory(showInventory: boolean) {
        this.showInventory = showInventory;
        this.configInventory();
    }

    public setPersistentSync(fn: AppFunction) {
        this.syncPersistent = fn;
    }

    private async configInventory() {

        const containsDate = (inv: Inventory, date: JsonDate | undefined) => {
            if (date === undefined) {
                return false;
            }
            if (inv.fromDate && inv.fromDate > date) {
                return false;
            }
            if (inv.toDate && inv.toDate < date) {
                return false;
            }
            return true;
        };
        const containsDates = (inv: Inventory) => {
            for (const d of this.dueDates) {
                if (containsDate(inv, d)) {
                    return true;
                }
            }
            if (containsDate(inv, this.today)) {
                return true;
            }
            return false;
        }

        if (! this.showInventory) {
            this.pickupInventory = undefined;
            this.deliveryInventory = undefined;
        } else {

            const inv = await this.server.getInventory();
            const allInventory = inv.filter(i => i.status == "Available" && i.active);

            const filter = (att: keyof Inventory) => (inv: Inventory) => {
                if (! inv[att]) {
                    return false;
                }
                if (! containsDates(inv)) {
                    return false;
                }
                return true;
            }

            this.pickupInventory = allInventory.filter(filter("forPickup"));
            this.deliveryInventory = allInventory.filter(filter("forDelivery"));

        }
        this.update();
    }

    public setToday(today: JsonDate | undefined) {
        this.today = this.userState.dueDate = today;
    }

    public addDuedate(date: JsonDate) {
        this.dueDates.add(date);
    }

    public setExcludedUndated() {
        this.excludeUndated = true;
    }

    async listen() {
        const deliveryDates = this.dueDates && this.dueDates.size > 0 ? Array.from(this.dueDates) : undefined;
        const opts = { noPast: this.noPast, silent: true, deliveryDate: deliveryDates,  deliveryDateSince: this.userState.deliveryDateSince};

        const listener = (orders: Order[]) => {
            this.refresh(orders);
            return {
                cancelListener: false
            }
        };

        this.subscription = subscribe(opts, listener, this.subscription);
    }

    async refresh(d: Order[] | false) {
        if (d) {
            this.orders = d;
        } else {
            sync();
            this.listen();
        }
        this.filterOrders();
        this.update([".actions", ".orders"]);
    }

    private applyFilter() {
        this.update();
    }

    filterOrders() {


        const u = this.userState;

        const prefilteredOrders = this.orders.filter((order) => {

            const s = u.search ? u.search.toLowerCase() : "";

            const searchOk = u.search == undefined || (JSON.stringify(order).toLowerCase().indexOf(s) >= 0);

            // @ts-ignore
            const delivery = isDelivery(order);

            const deliveryOk = !((u.delivery == "Delivery" && !delivery) || ((u.delivery == "Pickup") && delivery));

            let deliveryDateOk = true;
            if (u.dueDate != undefined) {
                deliveryDateOk = false;
                if (!order.deliveryDate && !this.excludeUndated) {
                    deliveryDateOk = true;
                }
                if (u.dueDate == order.deliveryDate) {
                    deliveryDateOk = true;
                }
            }

            if (this.dueDates.size > 0) {
                deliveryDateOk = order.deliveryDate ? false : true;
                if (order.deliveryDate && this.dueDates.has(order.deliveryDate)) {
                    deliveryDateOk = true;
                }
            }

            const filterOk = !this.filter || this.filter(order);

            const ok = searchOk && deliveryOk && deliveryDateOk && filterOk;

            return ok;
        });

        this.states = {
            "New": 0
        };
        const states = this.states;
        prefilteredOrders.forEach((order) => {
            const state = order.currentStatus as string;
            states[state] = (states[state] || 0) + 1;
        });

        if (Object.keys(states).length > 1) {
            states["All"] = prefilteredOrders.length;
        }

        if (!states[u.state] && !u.search && u.state != "New" && u.state != "All") {
            if (states["New"]) {
                u.state = "New";
            } else {
                u.state = Object.keys(states)[0];
            }
        }

        if (u.state && !states[u.state]) {
            states[u.state] = 0;
        }

        this.filteredOrders = prefilteredOrders.filter((order) => {

            const s = u.search ? u.search.toLowerCase() : "";

            const statusOk = (u.state == "All") || (order["currentStatus"] == u.state);
            const ok = statusOk;

            return ok;
        });

        const b = this.createFunctionBadge();
        if (b && badgeChange(this.lastBadge, b)) {
            this.lastBadge = b;
            this.notify(new BadgeNotification(b));
        }
    }

    protected createFunctionBadge() {
        if (! this.syncPersistent) {
            return undefined;
        }
        const b : FunctionBadge = {
            fn: this.syncPersistent,
            green: this.showGreenBadge ? this.filteredOrders.length : undefined,
            orange: this.filteredOrders.filter(o => o.currentStatus == "New").length,
        };

        if (this.showInventory) {
            b.red = this.filteredOrders.filter(o => ! o.inventoryHandled).length;
        } else {
            b.red = this.filteredOrders.filter(o => ! o.deliveryDate).length;
        }
        return b;
    }

    protected render0(): JQuery<HTMLElement> {

        const u = this.userState;

        const renderCreatedDate = (d: string) => {
            return format(parseISO(d), "yyyy-MM-dd HH:mm");
        };

        const renderDeliveryNote = (order: Order) => {
            const notes = [];
            for (const line of order.lineItems || []) {
                // @ts-ignore
                for (const note of line.customTextFields || []) {
                    if (note.title && note.title.startsWith("Pick up or delivery date")) {
                        notes.push(note.value);
                    }
                }
            }
            const n = notes.join(";")
            return n;
        };

        const getDriver = (order: Order) => {
            const hs = (order.orderHistory || []).filter(h => h.newStatus == "Out for delivery");
            let driver = hs.length > 0 ? hs[hs.length-1].createdBy : undefined;
            if (driver) {
                driver = driver.replace(/ \(casual\)/, "");
            }
            return driver;
        };

        this.filterOrders();

        let allSelected = true;
        let selectedCount = 0;
        this.filteredOrders.forEach((order) => {
            const selected  = u.selectedIds[order.id as string];
            if (! selected) {
                allSelected = false;
            } else {
                selectedCount ++;
            }
        });

        const data = {
            orders: this.filteredOrders,
            states: this.states,
            selected: u.selectedIds,
            renderCreatedDate: renderCreatedDate,
            renderDeliveryNote: renderDeliveryNote,
            state: u.state,
            isDelivery: isDelivery,
            delivery: u.delivery,
            dueDate: u.dueDate,
            deliveryDateSince: u.deliveryDateSince,
            today: this.today,
            formatDate: formatDate,
            search: u.search,
            allSelected: allSelected,
            selectedCount: selectedCount,
            showFilters: this.showFilters,
            emptyOrderMessage: this.emptyOrderMessage,
            deliveryAfter: this.deliveryAfter,
            deliveryBefore: this.deliveryBefore,
            getDriver: getDriver,
            showInventory: this.showInventory,
            deliveryInventory: this.deliveryInventory,
            pickupInventory: this.pickupInventory,
            renderName: renderName,
            renderDollars: renderDollars,
        };

        const out = this.pug(template, data);

        this.afterUpdate(() => {
            this.elt().find("input.datefield").datefield({startDay: 1});
        });

        u.persist();

        return out;
    }

    select(id: string, selected: boolean) {
        const u = this.userState;
        if (id == "all") {
            for (const order of this.filteredOrders) {
                u.selectedIds[order.id as string] = selected;
            }
            this.update([".orders", ".actions"]);
        } else {
            u.selectedIds[id] = selected;
            this.update([".actions"]);
        }
    }

    mapSelected() {
        const u = this.userState;
        const ids = this.filteredOrders.filter(order => u.selectedIds[order.id as string]).map(order => order.id);
        console.log("Map", ids);

        this.notify(new MapOrdersNotification(ids as string[]));
    }

    async printSelected() {
        const u = this.userState;
        const ids = this.filteredOrders.filter(order => u.selectedIds[order.id as string]).map(order => order.id);
        console.log("Print", ids);

        const missingDates = this.filteredOrders.filter(order => u.selectedIds[order.id as string] && ! order.deliveryDate);
        if (missingDates.length > 0) {
            const order = missingDates.length == 1 ? "order" : "orders";
            const date = missingDates.length == 1 ? "date" : "dates";
            const numbers = missingDates.map(o => o.number).join(", ");

            const result = await Alerts.alert({
                message: `Order date not set on ${order} ${numbers}. Set the order ${date} before printing!`
            });
        } else {
            this.notify(new PrintOrdersNotification(ids as string[]));
        }

    }

    async cardSelected() {
        const u = this.userState;
        const ids = this.filteredOrders.filter(order => u.selectedIds[order.id as string]).map(order => order.id as string);

        this.notify(new CardNotification(ids));
    }

    markPrintedSelected() {
        const u = this.userState;
        const history = {
            "newStatus": "Printed"
        }

        function alreadyPrinted(order: Order) {
            return order.orderHistory && order.orderHistory.filter(h => h.newStatus == "Printed").length > 0;
        }

        const ids = this.filteredOrders
            .filter(order=> u.selectedIds[order.id as string] && ! alreadyPrinted(order))
            .map(order => order.id);
        const total = ids.length;
        let cnt = 0;

        const records_  = ids.map((id) => this.server.recordStatus(id as string, history));
        const all_ = Promise.all(records_);
        all_.then(() => {
            this.refresh(false);
        });

    }

    async addToLoad() {
        const u = this.userState;
        const already = await getDeliveries();

        const orders = this.filteredOrders
            .filter(order=> u.selectedIds[order.id as string] && already.indexOf(order.id as any as string) == -1);

        const issues : string[] = [];

        for (const order of orders) {
            const today = toJSONDate(new Date());
            const deliveryDate = order.deliveryDate;
            if (deliveryDate && deliveryDate != today) {
                issues.push(`Delivery date for order #${order.number} is ${deliveryDate}. `);
            }
        }

        const YES = "Ok, continue";
        const NO = "Whoops, cancel";

        if (issues.length > 0) {
            const ok = await Alerts.confirmation({
                message: issues.join("\n"),
                options: [ YES, NO ]
            });
            if (ok === NO) {
                return;
            }
        }

        issues.length = 0;

        for (const order of orders) {
            if (hasExtras(order)) {
                issues.push(`Order #${order.number} has extras. `);
            }
        }

        if (issues.length > 0) {
            const ok = await Alerts.confirmation({
                message: issues.join("\n"),
                options: [ "Ok" ]
            });
        }

        for (const order of orders) {

            const id : string = order.id as any as string;

            await addDelivery(id);

            const history : OrderHistory = {
                newStatus: "Out for delivery"
            };

            await this.server.recordStatus(id, history);
        }

        this.refresh(false);

    }

    printAll() {
        const ids = this.orders.map(order => order.id);
        console.log("Print", ids);

        if (true) {
            this.notify(new PrintOrdersNotification(ids as string[]));
        } else {
            const url = new URL("api/orders/print/" + ids, new URL(window.location.href));
            window.location.href = url.toString();
        }
    }

    show(id: string) {
        this.notify(new ViewOrderNotification(id));
    }

    setState(state: string) {
        const u = this.userState;
        u.state = state;
        this.update();
    }
    setDelivery(delivery: "Any" | "Pickup" | "Delivery") {
        const u = this.userState;
        u.delivery = delivery;
        this.update();
    }
    setSearch(search: string) {
        const u = this.userState;
        u.search = search;
        // avoid updating search field...
        this.update([".orders", ".states"]);
    }
    setDueDate(date: JsonDate | undefined) {
        const u = this.userState;
        u.dueDate = date || undefined;
        this.update();
    }
    setDeliveryDateSince(date: JsonDate | undefined) {
        const u = this.userState;
        u.deliveryDateSince = date || undefined;
        this.refresh(false);
    }

    async setDeliveryDate(orderid: string, date: JsonDate | undefined) {

        let check: string[] = [];

        if (date) {

            const order = this.orders.filter(o => o.id == orderid)[0];
            if (order && order.lineItems) {

                const df = (order.lineItems[0].customTextFields || []).filter(f => f.title && f.title.toLowerCase().indexOf("delivery date") >= 0)[0];
                const dv = df && df.value;

                if (dv && ! dv.startsWith(date)) {
                    check.push(`The date on the order is ${dv} and you are setting ${date}.`);
                }
            }

            const now = new Date();
            const d = parseISO(date);
            const days = differenceInCalendarDays(d, now);
            if (days < 0) {
                check.push(date + " is in the past. ");
            }
        }

        let ok = true;
        if (check.length > 0) {
            const YES = "Yes, use the date";
            const result = await Alerts.confirmation({
                message: check.join("\n") + " Really set the date?",
                options: [ YES, "No, don't use the date"]
            });
            ok = result == YES;
        }

        if (ok) {
            const order_ = this.server.setDeliveryDate(orderid, date);
            order_.then((order) => {
                this.orders.forEach((o, idx) => {
                    if (o.id == orderid) {
                        this.orders[idx] = order;
                    }
                })
                this.update(".orders");
            });
        }

    }

    prepareSetClock(orderid: string) {

        const orders = this.orders.filter((o) => o.id === orderid);
        if (! orders.length) {
            return;
        }
        const order = orders[0];
        this.deliveryAfter = order.deliveryAfter;
        this.deliveryBefore = order.deliveryBefore;
        this.setClockOrderId = order.id;

        this.update(".set-clock-modal");
        this.elt().find(".set-clock-modal.js-modal").addClass("active");
    }

    setClock() {
        const orderid = this.setClockOrderId;
        if (! orderid) {
            return;
        }

        const form = this.elt().find(".set-clock-modal form");
        const data : any = {};

        formToJson(form, data);

        const order_ = this.server.setDeliveryTime(orderid, data.deliveryAfter, data.deliveryBefore);
        order_.then((order) => {-
            this.orders.forEach((o, idx) => {
                if (o.id == orderid) {
                    this.orders[idx] = order;
                }
            })
            this.update(".orders");
        });

    }

    editInventory(orderid: string) {
        const orders = this.orders.filter((o) => o.id === orderid);
        if (! orders.length) {
            return;
        }
        const order = orders[0];

        order.inventoryHandled = false;

        this.update();
    }

    async saveInventory(orderid: string, data: any) {

        const orders = this.orders.filter((o) => o.id === orderid);
        if (! orders.length) {
            return;
        }
        const order = orders[0];
        if (! order.lineItems) {
            return;
        }

        const uses: InventoryUse[] = [];
        for (const name in data) {
            uses.push({
                name: name,
                quantity: +data[name]
            });
        }

        order.inventoryHandled = true;

        const newOrder = await this.server.setInventoryUse(orderid, uses);

        this.orders.forEach((o, idx) => {
            if (o.id == orderid) {
                this.orders[idx] = newOrder;
            }
        });

        await this.configInventory();

        this.update(".orders");
    }
}

// Work around bug/weird behaviour with overflow-y on .filters which clips datepicker calendar popup
View.on(
    "." + OrdersView.classname,
    "click",
    ".controls .filters .datefield",
    function(that: OrdersView, event, elt) {
        that.elt().find(".filters").removeClass("overflow-x-auto");
    });

View.on(
    "." + OrdersView.classname,
    "change",
    "input.orderSelect",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        const selected = jQuery(elt).is(":checked");
        const id = jQuery(elt).data("id");
        that.select(id, selected);
    });


View.on(
    "." + OrdersView.classname,
    "click",
    ".print-selected",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        that.printSelected();
    });

View.on(
    "." + OrdersView.classname,
    "click",
    ".card-selected",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        that.cardSelected();
    });

View.on(
    "." + OrdersView.classname,
    "click",
    ".mark-printed-selected",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        that.markPrintedSelected();
    });

View.on(
    "." + OrdersView.classname,
    "click",
    ".add-to-load-selected",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        that.addToLoad();
    });

View.on(
    "." + OrdersView.classname,
    "click",
    ".map-selected",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        that.mapSelected();
    });

View.on(
    "." + OrdersView.classname,
    "click",
    ".sync",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        sync();
    });

View.on(
    "." + OrdersView.classname,
    "click",
    ".orderClickable",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        const id = jQuery(elt).closest(".order").data("orderid") as string;
        that.show(id);
    });

View.on(
    "." + OrdersView.classname,
    "change",
    "select[name='state']",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        const state = jQuery(elt).val() as string;
        that.setState(state);
    });

View.on(
    "." + OrdersView.classname,
    "change",
    "select[name='delivery']",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        const delivery = jQuery(elt).val() as "Any" | "Delivery" | "Pickup";
        that.setDelivery(delivery);
    });

View.on(
    "." + OrdersView.classname,
    "input",
    "input[name='search']",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        const search = jQuery(elt).val() as string;
        that.setSearch(search);
    });

View.on(
    "." + OrdersView.classname,
    "click",
    ".date-suggestion",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        const orderElt = jQuery(elt).closest(".order");
        const id = orderElt.data("orderid") as string;
        const days = jQuery(elt).data("suggestion") as number | undefined;
        if (days === undefined) {
            that.setDeliveryDate(id, undefined);
        } else {
            const date = add(new Date(), {days: days});
            const d = toJSONDate(date);
            that.setDeliveryDate(id, d);
        }
    });

View.on(
    "." + OrdersView.classname,
    "change",
    "input[name='deliveryDate']",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        const orderElt = jQuery(elt).closest(".order");
        const id = orderElt.data("orderid") as string;
        const ddmmyyyy = jQuery(elt).val() as string;
        const date = parseDate(ddmmyyyy);
        if (date) {
            that.setDeliveryDate(id, date);
        }
    });

View.on(
    "." + OrdersView.classname,
    "change",
    "input[name='due-date']",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        const ddmmyyyy = jQuery(elt).val() as string;
        const date = parseDate(ddmmyyyy);
        that.setDueDate(date);
        that.elt().find(".filters").addClass("overflow-x-auto");
    });
View.on(
    "." + OrdersView.classname,
    "change",
    "input[name='delivery-date-since']",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        const ddmmyyyy = jQuery(elt).val() as string;
        const date = parseDate(ddmmyyyy);
        that.setDeliveryDateSince(date);
        that.elt().find(".filters").addClass("overflow-x-auto");
    });

View.on(
    "." + OrdersView.classname,
    "click",
    ".set-clock",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        const orderid = jQuery(elt).closest(".order").data("orderid") as string;
        that.prepareSetClock(orderid);
    });
View.on(
    "." + OrdersView.classname,
    "click",
    ".set-clock-modal button.save",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        event.stopImmediatePropagation();
        that.setClock();
    });

View.on(
    "." + OrdersView.classname,
    "click",
    ".edit-inventory",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const orderid = jQuery(elt).closest(".order").data("orderid") as string;
        that.editInventory(orderid);
    });

View.on(
    "." + OrdersView.classname,
    "click",
    ".save-inventory",
    function(that: OrdersView, event, elt) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const orderid = jQuery(elt).closest(".order").data("orderid") as string;
        const container = jQuery(elt).closest(".inventory form");
        const data : any = {};
        formToJson(container, data);
        that.saveInventory(orderid, data);
    });
